import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid, Skeleton, Snackbar, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { Layout } from 'features/common/components/Layout';
import routes from 'constants/routes';
import { CarrierSelectorList } from 'features/authorization/components/AirlineMerchantReference/CarrierSelectorList';
import { CarrierConditionsPanel } from 'features/authorization/components/AirlineMerchantReference/CarrierConditionsPanel';
import { CarrierReferenceItem } from 'features/authorization/helpers/carrierReference';
import { CardDeskErrorBoundary } from 'features/cardDesk/components/CardDeskErrorBoundary';
import { MerchantReferenceStatus } from 'features/cardDesk/components/MerchantReferenceStatus';
import { useMerchantReference } from 'features/cardDesk/hooks/useMerchantReference';

interface RouteParams {
	carrierCode?: string;
}

const carrierPath = (code: string): string =>
	routes.airlines.merchantAndInstallmentsByCarrier.replace(':carrierCode', code);

const parseCarrierFromPathname = (pathname: string): string | null => {
	const base = routes.airlines.merchantAndInstallments;
	if (!pathname.startsWith(base)) return null;
	const suffix = pathname.slice(base.length).replace(/^\//, '');
	return suffix ? suffix.toUpperCase() : null;
};

const resolveInitialCarrierCode = (
	carrierCodeParam: string | undefined,
	carriers: CarrierReferenceItem[],
): string | null => {
	if (!carrierCodeParam) return null;
	const normalized = carrierCodeParam.toUpperCase();
	return carriers.some((c) => c.code === normalized) ? normalized : null;
};

const AirlineMerchantReferencePage = (): JSX.Element => {
	const { carrierCode: carrierCodeParam } = useParams<RouteParams>();
	// Las reglas de la base (Reglas de cruce) o, si la Support API no responde, la tabla fija del Power.
	const { carriers, source, fixed } = useMerchantReference();
	const [selectedCode, setSelectedCode] = useState<string | null>(() =>
		resolveInitialCarrierCode(carrierCodeParam, carriers),
	);
	const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

	useEffect(() => {
		const fromUrl = resolveInitialCarrierCode(carrierCodeParam, carriers);
		if (fromUrl) setSelectedCode(fromUrl);
	}, [carrierCodeParam, carriers]);

	useEffect(() => {
		const onPopState = (): void => {
			const code = parseCarrierFromPathname(window.location.pathname);
			if (code && carriers.some((c) => c.code === code)) {
				setSelectedCode(code);
			} else if (!code) {
				setSelectedCode(null);
			}
		};
		window.addEventListener('popstate', onPopState);
		return () => window.removeEventListener('popstate', onPopState);
	}, [carriers]);

	const handleSelectCarrier = useCallback((code: string) => {
		setSelectedCode(code);
		// No usar history.push: BrowserRouter tiene forceRefresh y recarga toda la página.
		window.history.replaceState(null, '', carrierPath(code));
	}, []);

	const handleCopied = useCallback((message: string) => {
		setSnackbarMessage(message);
	}, []);

	const carrierGrid = (list: CarrierReferenceItem[]): JSX.Element => (
		<Grid container spacing={3}>
			<Grid item xs={12} md={4} lg={3}>
				<CarrierSelectorList carriers={list} selectedCode={selectedCode} onSelect={handleSelectCarrier} />
			</Grid>
			<Grid item xs={12} md={8} lg={9}>
				<CarrierConditionsPanel carrier={list.find((c) => c.code === selectedCode) ?? null} onCopied={handleCopied} />
			</Grid>
		</Grid>
	);

	return (
		<Layout>
			<Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1400, mx: 'auto' }}>
				<Typography variant="h4" fontWeight={500} gutterBottom>
					Códigos de comercio y cuotas
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
					Condiciones por validating carrier: canal de autorización (Fiserv, call center o GDS), número de comercio y
					planes de cuotas habilitados.
				</Typography>

				<MerchantReferenceStatus source={source} />

				{source === 'loading' ? (
					<Skeleton variant="rectangular" height={320} aria-label="Cargando las reglas" />
				) : (
					// Si las de la base se rompen al dibujarse, la tabla fija con el aviso: la página nunca queda en blanco.
					<CardDeskErrorBoundary
						fallback={
							<>
								<MerchantReferenceStatus source="fixed" />
								{carrierGrid(fixed)}
							</>
						}
					>
						{carrierGrid(carriers)}
					</CardDeskErrorBoundary>
				)}
			</Box>

			<Snackbar
				open={Boolean(snackbarMessage)}
				autoHideDuration={3000}
				onClose={() => setSnackbarMessage(null)}
				message={snackbarMessage}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			/>
		</Layout>
	);
};

export default AirlineMerchantReferencePage;
